import {
  accountSetupStatusSchema,
  activationPreviewSchema,
  loginResponseSchema,
  recoveryCodesSchema,
  totpEnrollmentSchema,
  type AccountSetupStatus,
  type ActivationPreview,
  type LoginResponse,
  type TotpEnrollment,
} from "@kavoosh/contracts";

const API_BASE = import.meta.env.VITE_API_BASE ?? "/api/v1";

function cookie(name: string): string | undefined {
  return document.cookie.split(";").map((item) => item.trim())
    .find((item) => item.startsWith(`${name}=`))?.slice(name.length + 1);
}

async function setupRequest(path: string, init?: RequestInit): Promise<Response> {
  const csrf = cookie("kavoosh_setup_csrf");
  const response = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    cache: "no-store",
    ...init,
    headers: {
      Accept: "application/json",
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...(csrf ? { "X-Setup-CSRF-Token": decodeURIComponent(csrf) } : {}),
      ...init?.headers,
    },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { detail?: unknown };
    if (typeof body.detail === "string") throw new Error(body.detail);
    if (typeof body.detail === "object" && body.detail !== null
      && "message" in body.detail && typeof body.detail.message === "string") {
      throw new Error(body.detail.message);
    }
    throw new Error("Account setup could not be completed.");
  }
  return response;
}

export async function previewActivation(token: string): Promise<ActivationPreview> {
  const response = await fetch(`${API_BASE}/auth/activation?token=${encodeURIComponent(token)}`, {
    credentials: "omit",
    cache: "no-store",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) throw new Error("This activation link is invalid, expired, or already used.");
  return activationPreviewSchema.parse(await response.json());
}

export async function startActivation(token: string): Promise<AccountSetupStatus> {
  const response = await fetch(`${API_BASE}/auth/activation/session`, {
    method: "POST",
    credentials: "include",
    cache: "no-store",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
  if (!response.ok) throw new Error("This activation link can no longer start account setup.");
  return accountSetupStatusSchema.parse(await response.json());
}

export async function getAccountSetupStatus(): Promise<AccountSetupStatus> {
  const response = await setupRequest("/auth/setup");
  return accountSetupStatusSchema.parse(await response.json());
}

export async function setAccountSetupPassword(input: {
  currentPassword?: string;
  newPassword: string;
}): Promise<AccountSetupStatus> {
  const response = await setupRequest("/auth/setup/password", {
    method: "POST",
    body: JSON.stringify({
      current_password: input.currentPassword ?? null,
      new_password: input.newPassword,
    }),
  });
  return accountSetupStatusSchema.parse(await response.json());
}

export async function beginSetupTotp(): Promise<TotpEnrollment> {
  const response = await setupRequest("/auth/setup/mfa/totp/enrollment", {
    method: "POST",
    body: JSON.stringify({ label: "Kavoosh Authenticator" }),
  });
  return totpEnrollmentSchema.parse(await response.json());
}

export async function confirmSetupTotp(factorId: string, code: string): Promise<string[]> {
  const response = await setupRequest("/auth/setup/mfa/totp/confirmation", {
    method: "POST",
    body: JSON.stringify({ factor_id: factorId, code }),
  });
  return recoveryCodesSchema.parse(await response.json()).recovery_codes;
}

export async function acknowledgeSetupRecoveryCodes(): Promise<AccountSetupStatus> {
  const response = await setupRequest("/auth/setup/recovery-codes/acknowledgement", {
    method: "POST",
    body: "{}",
  });
  return accountSetupStatusSchema.parse(await response.json());
}

export async function reissueSetupRecoveryCodes(): Promise<string[]> {
  const response = await setupRequest("/auth/setup/recovery-codes/reissue", {
    method: "POST",
    body: "{}",
  });
  return recoveryCodesSchema.parse(await response.json()).recovery_codes;
}

export async function completeAccountSetup(): Promise<LoginResponse> {
  const response = await setupRequest("/auth/setup/complete", {
    method: "POST",
    body: "{}",
  });
  return loginResponseSchema.parse(await response.json());
}
